import { NumericInputConstraint } from "./areaInput";

export type ListingConstraintType = "house" | "land_plot" | "apartment" | "commercial";
export type ListingMoneyField =
  | "price_per_sqm"
  | "agent_commission_percent"
  | "agent_commission_amount"
  | "initial_payment"
  | "payment"
  | "full_payment_purchase"
  | "full_payment_price"
  | "agent_markup"
  | "recalculation_percentage"
  | "assignment_paid_amount";

export type StringInputConstraint = {
  min_length: number;
  max_length: number;
  trim?: boolean;
  nullable?: boolean;
  required?: boolean;
  required_for?: string[];
};

export type NumberInputConstraint = NumericInputConstraint & {
  integer: boolean;
  nullable?: boolean;
  required?: boolean;
  required_for?: string[];
  optional_for?: string[];
};

type AreaConstraint = {
  decimal_places: number;
  by_listing_type: Partial<Record<ListingConstraintType, NumericInputConstraint>>;
};

export type ListingConstraints = {
  version: number;
  request: {
    max_body_bytes: number;
    image_files_included: boolean;
  };
  taxonomy: {
    endpoint: string;
    combination_must_exist: boolean;
  };
  creation: {
    forced_status_id: number;
    forced_moderation_status: string;
  };
  fields: {
    title: StringInputConstraint;
    description: StringInputConstraint;
    longitude: NumberInputConstraint;
    latitude: NumberInputConstraint;
    owner_id: NumberInputConstraint;
    type_id: NumberInputConstraint;
    purpose_id: NumberInputConstraint;
    room_count: NumberInputConstraint;
    floor: NumberInputConstraint;
    floor_count: NumberInputConstraint;
    construction_year: NumberInputConstraint;
    completion_year: NumberInputConstraint;
    window_view: StringInputConstraint;
  };
  areas: {
    area_sqm: AreaConstraint & { unit: "sqm" };
    area_site: AreaConstraint & {
      storage_unit: "sqm";
      display_unit: "sotka";
      square_meters_per_sotka: number;
    };
  };
  money: {
    currency: "RUB";
    decimal_places: number;
    payment_terms: {
      max_items: number;
      unique_by: string;
      periods: Record<"sqm" | "monthly", NumericInputConstraint>;
    };
    fields: Record<ListingMoneyField, NumericInputConstraint>;
  };
  images: {
    max_items: number;
    url: StringInputConstraint & { protocols: string[] };
    sort_order: { min: number; integer: boolean; unique: boolean };
    primary_rule: string;
    image_url_must_match_sort_order_zero: boolean;
  };
  addons: {
    boolean_fields: string[];
    address_fields: { names: string[]; max_length: number };
    house_kind: { max_length: number };
    utilities_payer: { enum: string[]; required_for: string[] };
    rent_term: { enum: string[]; required_for: string[] };
    assignment_months_left: NumberInputConstraint;
    assignment_paid_months: NumberInputConstraint;
  };
  assignment: {
    allowed_for_board_category: string;
    currency_id: NumberInputConstraint;
    recalculation_period: NumberInputConstraint;
  };
  cross_field_rules: string[];
};

const CURRENT_YEAR = new Date().getUTCFullYear();
const MAX_SAFE_ID = Number.MAX_SAFE_INTEGER;

const integerConstraint = (min: number, max: number): NumberInputConstraint => ({
  min,
  max,
  decimal_places: 0,
  integer: true,
});

export const FALLBACK_LISTING_CONSTRAINTS: ListingConstraints = {
  version: 2,
  request: { max_body_bytes: 1_048_576, image_files_included: false },
  taxonomy: { endpoint: "/v1/listings/taxonomy", combination_must_exist: true },
  creation: { forced_status_id: 2, forced_moderation_status: "pending" },
  fields: {
    title: { min_length: 5, max_length: 150, trim: true, required: true },
    description: { min_length: 0, max_length: 5_000, trim: true, nullable: true },
    longitude: { min: -180, max: 180, decimal_places: 6, integer: false, required: true },
    latitude: { min: -90, max: 90, decimal_places: 6, integer: false, required: true },
    owner_id: { ...integerConstraint(1, MAX_SAFE_ID), required: true },
    type_id: { ...integerConstraint(1, MAX_SAFE_ID), required_for: ["house"] },
    purpose_id: { ...integerConstraint(1, MAX_SAFE_ID), required_for: ["land_plot"] },
    room_count: { ...integerConstraint(1, 50), required_for: ["house", "apartment"] },
    floor: { ...integerConstraint(-5, 300), required_for: ["apartment", "commercial"] },
    floor_count: {
      ...integerConstraint(1, 300),
      required_for: ["house", "commercial"],
      optional_for: ["apartment"],
    },
    construction_year: {
      ...integerConstraint(1800, CURRENT_YEAR + 1),
      nullable: true,
      required: false,
    },
    completion_year: {
      ...integerConstraint(CURRENT_YEAR - 1, CURRENT_YEAR + 10),
      required_for: ["assignment"],
    },
    window_view: {
      min_length: 1,
      max_length: 100,
      trim: true,
      required_for: ["apartment", "commercial"],
    },
  },
  areas: {
    area_sqm: {
      unit: "sqm",
      decimal_places: 2,
      by_listing_type: {
        house: { min: 10, max: 20_000, decimal_places: 2 },
        apartment: { min: 5, max: 5_000, decimal_places: 2 },
        commercial: { min: 1, max: 1_000_000, decimal_places: 2 },
      },
    },
    area_site: {
      storage_unit: "sqm",
      display_unit: "sotka",
      square_meters_per_sotka: 100,
      decimal_places: 2,
      by_listing_type: {
        house: { min: 1, max: 1_000_000, decimal_places: 2 },
        land_plot: { min: 1, max: 10_000_000, decimal_places: 2 },
      },
    },
  },
  money: {
    currency: "RUB",
    decimal_places: 2,
    payment_terms: {
      max_items: 10,
      unique_by: "period",
      periods: {
        sqm: { min: 100, max: 100_000_000, decimal_places: 2 },
        monthly: { min: 1_000, max: 100_000_000, decimal_places: 2 },
      },
    },
    fields: {
      price_per_sqm: { min: 100, max: 100_000_000, decimal_places: 2 },
      agent_commission_percent: { min: 0, max: 100, decimal_places: 2 },
      agent_commission_amount: { min: 0, max: 100_000_000, decimal_places: 2 },
      initial_payment: { min: 0, max: 1_000_000_000_000, decimal_places: 0 },
      payment: { min: 0, max: 100_000_000, decimal_places: 0 },
      full_payment_purchase: { min: 0, max: 1_000_000_000_000, decimal_places: 0 },
      full_payment_price: { min: 0, max: 1_000_000_000_000, decimal_places: 0 },
      agent_markup: { min: 0, max: 1_000_000_000_000, decimal_places: 0 },
      recalculation_percentage: { min: 0, max: 100, decimal_places: 0 },
      assignment_paid_amount: { min: 0, max: 1_000_000_000_000, decimal_places: 0 },
    },
  },
  images: {
    max_items: 30,
    url: {
      min_length: 1,
      max_length: 2_048,
      protocols: ["http", "https"],
    },
    sort_order: { min: 0, integer: true, unique: true },
    primary_rule: "sort_order_zero_only",
    image_url_must_match_sort_order_zero: true,
  },
  addons: {
    boolean_fields: [
      "is_renovated",
      "is_penthouse",
      "is_apartment_unit",
      "is_view",
      "is_completed",
      "has_pool",
      "has_bathhouse",
      "has_sewerage",
      "has_water",
      "has_electricity",
      "has_gas",
      "utility_included",
    ],
    address_fields: {
      names: ["address_region", "address_locality", "address_street", "address_house"],
      max_length: 160,
    },
    house_kind: { max_length: 100 },
    utilities_payer: { enum: ["tenant", "landlord"], required_for: ["rent"] },
    rent_term: {
      enum: ["short_term", "long_term"],
      required_for: ["non_commercial_rent"],
    },
    assignment_months_left: integerConstraint(0, 1_200),
    assignment_paid_months: integerConstraint(0, 1_200),
  },
  assignment: {
    allowed_for_board_category: "assignments",
    currency_id: integerConstraint(1, MAX_SAFE_ID),
    recalculation_period: integerConstraint(0, 1_200),
  },
  cross_field_rules: [
    "floor_must_not_exceed_floor_count_when_both_are_positive",
    "full_payment_price_must_cover_initial_payment",
    "full_payment_price_must_cover_full_payment_purchase",
    "taxonomy_combination_must_exist",
    "land_plot_must_not_have_year_fields",
  ],
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeInteger = (value: unknown, fallback: number, min = 0): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= min ? parsed : fallback;
};

const normalizeDecimalPlaces = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 20 ? parsed : fallback;
};

const normalizeStringArray = (value: unknown, fallback: string[]): string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string") ? [...value] : [...fallback];

const normalizeNumericConstraint = (
  value: unknown,
  fallback: NumericInputConstraint,
  inheritedDecimalPlaces?: number,
): NumericInputConstraint => {
  if (!isRecord(value)) return { ...fallback };
  const min = Number(value.min);
  const max = Number(value.max);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return { ...fallback };
  return {
    min,
    max,
    decimal_places: normalizeDecimalPlaces(
      value.decimal_places,
      inheritedDecimalPlaces ?? fallback.decimal_places,
    ),
  };
};

const normalizeNumberField = (value: unknown, fallback: NumberInputConstraint): NumberInputConstraint => {
  const source = isRecord(value) ? value : {};
  const normalized: NumberInputConstraint = {
    ...fallback,
    ...normalizeNumericConstraint(source, fallback, source.integer === true ? 0 : undefined),
    integer: typeof source.integer === "boolean" ? source.integer : fallback.integer,
  };
  if (typeof source.required === "boolean" || fallback.required !== undefined) {
    normalized.required = typeof source.required === "boolean" ? source.required : fallback.required;
  }
  if (typeof source.nullable === "boolean" || fallback.nullable !== undefined) {
    normalized.nullable = typeof source.nullable === "boolean" ? source.nullable : fallback.nullable;
  }
  if (Array.isArray(source.required_for) || fallback.required_for) {
    normalized.required_for = normalizeStringArray(source.required_for, fallback.required_for || []);
  }
  if (Array.isArray(source.optional_for) || fallback.optional_for) {
    normalized.optional_for = normalizeStringArray(source.optional_for, fallback.optional_for || []);
  }
  return normalized;
};

const normalizeStringField = (value: unknown, fallback: StringInputConstraint): StringInputConstraint => {
  if (!isRecord(value)) return { ...fallback };
  const minLength = normalizeInteger(value.min_length, fallback.min_length);
  const maxLength = normalizeInteger(value.max_length, fallback.max_length);
  if (minLength > maxLength) return { ...fallback };
  const normalized: StringInputConstraint = {
    min_length: minLength,
    max_length: maxLength,
  };
  if (typeof value.trim === "boolean" || fallback.trim !== undefined) {
    normalized.trim = typeof value.trim === "boolean" ? value.trim : fallback.trim;
  }
  if (typeof value.nullable === "boolean" || fallback.nullable !== undefined) {
    normalized.nullable = typeof value.nullable === "boolean" ? value.nullable : fallback.nullable;
  }
  if (typeof value.required === "boolean" || fallback.required !== undefined) {
    normalized.required = typeof value.required === "boolean" ? value.required : fallback.required;
  }
  if (Array.isArray(value.required_for) || fallback.required_for) {
    normalized.required_for = normalizeStringArray(value.required_for, fallback.required_for || []);
  }
  return normalized;
};

const normalizeAreaConstraint = (value: unknown, fallback: AreaConstraint): AreaConstraint => {
  const source = isRecord(value) ? value : {};
  const byType = isRecord(source.by_listing_type) ? source.by_listing_type : {};
  const decimalPlaces = normalizeDecimalPlaces(source.decimal_places, fallback.decimal_places);
  return {
    decimal_places: decimalPlaces,
    by_listing_type: Object.fromEntries(
      Object.entries(fallback.by_listing_type).map(([listingType, fallbackRule]) => [
        listingType,
        normalizeNumericConstraint(byType[listingType], fallbackRule as NumericInputConstraint, decimalPlaces),
      ]),
    ) as Partial<Record<ListingConstraintType, NumericInputConstraint>>,
  };
};

export const normalizeListingConstraints = (value: unknown): ListingConstraints => {
  const source = isRecord(value) ? value : {};
  const request = isRecord(source.request) ? source.request : {};
  const taxonomy = isRecord(source.taxonomy) ? source.taxonomy : {};
  const creation = isRecord(source.creation) ? source.creation : {};
  const fields = isRecord(source.fields) ? source.fields : {};
  const areas = isRecord(source.areas) ? source.areas : {};
  const money = isRecord(source.money) ? source.money : {};
  const paymentTerms = isRecord(money.payment_terms) ? money.payment_terms : {};
  const periods = isRecord(paymentTerms.periods) ? paymentTerms.periods : {};
  const moneyFields = isRecord(money.fields) ? money.fields : {};
  const images = isRecord(source.images) ? source.images : {};
  const imageUrl = isRecord(images.url) ? images.url : {};
  const imageSortOrder = isRecord(images.sort_order) ? images.sort_order : {};
  const addons = isRecord(source.addons) ? source.addons : {};
  const addressFields = isRecord(addons.address_fields) ? addons.address_fields : {};
  const houseKind = isRecord(addons.house_kind) ? addons.house_kind : {};
  const utilitiesPayer = isRecord(addons.utilities_payer) ? addons.utilities_payer : {};
  const rentTerm = isRecord(addons.rent_term) ? addons.rent_term : {};
  const assignment = isRecord(source.assignment) ? source.assignment : {};
  const fallbackFields = FALLBACK_LISTING_CONSTRAINTS.fields;
  const areaSqm = normalizeAreaConstraint(areas.area_sqm, FALLBACK_LISTING_CONSTRAINTS.areas.area_sqm);
  const areaSiteSource = isRecord(areas.area_site) ? areas.area_site : {};
  const areaSite = normalizeAreaConstraint(areaSiteSource, FALLBACK_LISTING_CONSTRAINTS.areas.area_site);

  return {
    version: normalizeInteger(source.version, FALLBACK_LISTING_CONSTRAINTS.version, 1),
    request: {
      max_body_bytes: normalizeInteger(
        request.max_body_bytes,
        FALLBACK_LISTING_CONSTRAINTS.request.max_body_bytes,
        1,
      ),
      image_files_included:
        typeof request.image_files_included === "boolean"
          ? request.image_files_included
          : FALLBACK_LISTING_CONSTRAINTS.request.image_files_included,
    },
    taxonomy: {
      endpoint: typeof taxonomy.endpoint === "string" ? taxonomy.endpoint : FALLBACK_LISTING_CONSTRAINTS.taxonomy.endpoint,
      combination_must_exist:
        typeof taxonomy.combination_must_exist === "boolean"
          ? taxonomy.combination_must_exist
          : FALLBACK_LISTING_CONSTRAINTS.taxonomy.combination_must_exist,
    },
    creation: {
      forced_status_id: normalizeInteger(
        creation.forced_status_id,
        FALLBACK_LISTING_CONSTRAINTS.creation.forced_status_id,
        1,
      ),
      forced_moderation_status:
        typeof creation.forced_moderation_status === "string"
          ? creation.forced_moderation_status
          : FALLBACK_LISTING_CONSTRAINTS.creation.forced_moderation_status,
    },
    fields: {
      title: normalizeStringField(fields.title, fallbackFields.title),
      description: normalizeStringField(fields.description, fallbackFields.description),
      longitude: normalizeNumberField(fields.longitude, fallbackFields.longitude),
      latitude: normalizeNumberField(fields.latitude, fallbackFields.latitude),
      owner_id: normalizeNumberField(fields.owner_id, fallbackFields.owner_id),
      type_id: normalizeNumberField(fields.type_id, fallbackFields.type_id),
      purpose_id: normalizeNumberField(fields.purpose_id, fallbackFields.purpose_id),
      room_count: normalizeNumberField(fields.room_count, fallbackFields.room_count),
      floor: normalizeNumberField(fields.floor, fallbackFields.floor),
      floor_count: normalizeNumberField(fields.floor_count, fallbackFields.floor_count),
      construction_year: normalizeNumberField(fields.construction_year, fallbackFields.construction_year),
      completion_year: normalizeNumberField(fields.completion_year, fallbackFields.completion_year),
      window_view: normalizeStringField(fields.window_view, fallbackFields.window_view),
    },
    areas: {
      area_sqm: { ...areaSqm, unit: "sqm" },
      area_site: {
        ...areaSite,
        storage_unit: "sqm",
        display_unit: "sotka",
        square_meters_per_sotka: normalizeInteger(
          areaSiteSource.square_meters_per_sotka,
          FALLBACK_LISTING_CONSTRAINTS.areas.area_site.square_meters_per_sotka,
          1,
        ),
      },
    },
    money: {
      currency: "RUB",
      decimal_places: normalizeDecimalPlaces(money.decimal_places, FALLBACK_LISTING_CONSTRAINTS.money.decimal_places),
      payment_terms: {
        max_items: normalizeInteger(
          paymentTerms.max_items,
          FALLBACK_LISTING_CONSTRAINTS.money.payment_terms.max_items,
          1,
        ),
        unique_by:
          typeof paymentTerms.unique_by === "string"
            ? paymentTerms.unique_by
            : FALLBACK_LISTING_CONSTRAINTS.money.payment_terms.unique_by,
        periods: {
          sqm: normalizeNumericConstraint(periods.sqm, FALLBACK_LISTING_CONSTRAINTS.money.payment_terms.periods.sqm),
          monthly: normalizeNumericConstraint(
            periods.monthly,
            FALLBACK_LISTING_CONSTRAINTS.money.payment_terms.periods.monthly,
          ),
        },
      },
      fields: Object.fromEntries(
        Object.entries(FALLBACK_LISTING_CONSTRAINTS.money.fields).map(([field, fallbackRule]) => [
          field,
          normalizeNumericConstraint(moneyFields[field], fallbackRule),
        ]),
      ) as Record<ListingMoneyField, NumericInputConstraint>,
    },
    images: {
      max_items: normalizeInteger(images.max_items, FALLBACK_LISTING_CONSTRAINTS.images.max_items, 1),
      url: {
        ...normalizeStringField(imageUrl, FALLBACK_LISTING_CONSTRAINTS.images.url),
        protocols: normalizeStringArray(imageUrl.protocols, FALLBACK_LISTING_CONSTRAINTS.images.url.protocols),
      },
      sort_order: {
        min: normalizeInteger(imageSortOrder.min, FALLBACK_LISTING_CONSTRAINTS.images.sort_order.min),
        integer:
          typeof imageSortOrder.integer === "boolean"
            ? imageSortOrder.integer
            : FALLBACK_LISTING_CONSTRAINTS.images.sort_order.integer,
        unique:
          typeof imageSortOrder.unique === "boolean"
            ? imageSortOrder.unique
            : FALLBACK_LISTING_CONSTRAINTS.images.sort_order.unique,
      },
      primary_rule:
        typeof images.primary_rule === "string" ? images.primary_rule : FALLBACK_LISTING_CONSTRAINTS.images.primary_rule,
      image_url_must_match_sort_order_zero:
        typeof images.image_url_must_match_sort_order_zero === "boolean"
          ? images.image_url_must_match_sort_order_zero
          : FALLBACK_LISTING_CONSTRAINTS.images.image_url_must_match_sort_order_zero,
    },
    addons: {
      boolean_fields: normalizeStringArray(addons.boolean_fields, FALLBACK_LISTING_CONSTRAINTS.addons.boolean_fields),
      address_fields: {
        names: normalizeStringArray(addressFields.names, FALLBACK_LISTING_CONSTRAINTS.addons.address_fields.names),
        max_length: normalizeInteger(
          addressFields.max_length,
          FALLBACK_LISTING_CONSTRAINTS.addons.address_fields.max_length,
        ),
      },
      house_kind: {
        max_length: normalizeInteger(
          houseKind.max_length,
          FALLBACK_LISTING_CONSTRAINTS.addons.house_kind.max_length,
        ),
      },
      utilities_payer: {
        enum: normalizeStringArray(utilitiesPayer.enum, FALLBACK_LISTING_CONSTRAINTS.addons.utilities_payer.enum),
        required_for: normalizeStringArray(
          utilitiesPayer.required_for,
          FALLBACK_LISTING_CONSTRAINTS.addons.utilities_payer.required_for,
        ),
      },
      rent_term: {
        enum: normalizeStringArray(rentTerm.enum, FALLBACK_LISTING_CONSTRAINTS.addons.rent_term.enum),
        required_for: normalizeStringArray(rentTerm.required_for, FALLBACK_LISTING_CONSTRAINTS.addons.rent_term.required_for),
      },
      assignment_months_left: normalizeNumberField(
        addons.assignment_months_left,
        FALLBACK_LISTING_CONSTRAINTS.addons.assignment_months_left,
      ),
      assignment_paid_months: normalizeNumberField(
        addons.assignment_paid_months,
        FALLBACK_LISTING_CONSTRAINTS.addons.assignment_paid_months,
      ),
    },
    assignment: {
      allowed_for_board_category:
        typeof assignment.allowed_for_board_category === "string"
          ? assignment.allowed_for_board_category
          : FALLBACK_LISTING_CONSTRAINTS.assignment.allowed_for_board_category,
      currency_id: normalizeNumberField(
        assignment.currency_id,
        FALLBACK_LISTING_CONSTRAINTS.assignment.currency_id,
      ),
      recalculation_period: normalizeNumberField(
        assignment.recalculation_period,
        FALLBACK_LISTING_CONSTRAINTS.assignment.recalculation_period,
      ),
    },
    cross_field_rules: normalizeStringArray(
      source.cross_field_rules,
      FALLBACK_LISTING_CONSTRAINTS.cross_field_rules,
    ),
  };
};

export const getStringInputError = (
  value: unknown,
  constraint: StringInputConstraint,
  options: { required?: boolean; valueName?: string } = {},
): string | null => {
  const rawValue = String(value ?? "");
  const normalizedValue = constraint.trim ? rawValue.trim() : rawValue;
  const valueName = options.valueName || "Значение";

  if (!normalizedValue) {
    return options.required ? `Укажите ${valueName.toLowerCase()}.` : null;
  }
  if (normalizedValue.length < constraint.min_length) {
    return `Минимальная длина поля «${valueName}» — ${constraint.min_length} символов.`;
  }
  if (normalizedValue.length > constraint.max_length) {
    return `Максимальная длина поля «${valueName}» — ${constraint.max_length} символов.`;
  }
  return null;
};

export const getStringConstraintHint = (constraint: StringInputConstraint): string =>
  constraint.min_length > 0
    ? `От ${constraint.min_length} до ${constraint.max_length} символов`
    : `До ${constraint.max_length} символов`;

export const getUrlInputError = (
  value: unknown,
  constraint: StringInputConstraint & { protocols: string[] },
): string | null => {
  const stringError = getStringInputError(value, constraint, { valueName: "Ссылка на изображение" });
  if (stringError) return stringError;
  if (!value) return null;

  try {
    const url = new URL(String(value));
    return constraint.protocols.includes(url.protocol.replace(":", ""))
      ? null
      : `Допустимы только ссылки ${constraint.protocols.join("/").toUpperCase()}.`;
  } catch {
    return "Некорректная ссылка на изображение.";
  }
};

export const roundToDecimalPlaces = (value: number, decimalPlaces: number): number => {
  const factor = 10 ** decimalPlaces;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};
